import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { getDataGridStyles } from "@/theme";
import { TranscriptSectionIndexDB } from "../../classes/audioDb";
import LocalRecordingActions from "./LocalRecordingActions";
import InfoCircleIcon from "@icons/info-circle.svg?react";
import HelpCircleIcon from "@icons/help-circle.svg?react";
import { Tooltip } from "react-tooltip";
import { VITE_PUBLIC_ENV } from "@/common/utils/constants";
import { formatBytes } from "@/features/patient-view/components/documents/editor/utils";
import { formatDateAndTime, timeAgo } from "@/common/utils/helpers";
import { PatientRecord } from "@/common/utils/types";

interface LocalRecordingsProps {
    recordings: TranscriptSectionIndexDB[];
    handleAddTestUpload: () => void;
    handleUpload: (recording: TranscriptSectionIndexDB, existingPatientRecord: PatientRecord | null) => Promise<void>;
    handleDownload: (recording: TranscriptSectionIndexDB) => void;
    handleDelete: (recording: TranscriptSectionIndexDB) => Promise<void>;
}

const LocalRecordings: React.FC<LocalRecordingsProps> = ({
    recordings,
    handleAddTestUpload,
    handleUpload,
    handleDownload,
    handleDelete,
}) => {
    const columns: GridColDef[] = [
        {
            field: "patientName",
            headerName: "CLIPS",
            flex: 4,
            editable: false,
            sortable: false,
            disableColumnMenu: true,
            headerAlign: "left",
            align: "left",
            headerClassName: "datagrid-table--header",
            cellClassName: "datagrid-table--column-cell",
            renderCell: ({ row }: { row: TranscriptSectionIndexDB }) => (
                <div className="flex flex-col">
                    <span className="text-sm text-gray-900 font-medium">{row.patientName || "-"}</span>
                    <span className="text-xs text-gray-600">{row.fileName || "file name"}</span>
                </div>
            ),
        },
        {
            field: "createdAt",
            headerName: "TIMESTAMP",
            flex: 2,
            editable: false,
            sortable: false,
            disableColumnMenu: true,
            headerAlign: "left",
            align: "left",
            headerClassName: "datagrid-table--header",
            cellClassName: "datagrid-table--column-cell",
            renderCell: ({ row }: { row: TranscriptSectionIndexDB }) => (
                <div className="flex flex-col">
                    <span className="text-sm text-gray-900 font-medium">
                        {formatDateAndTime(row.createdAt.toISOString())}
                    </span>
                    <span className="text-xs text-gray-600">{timeAgo(row.createdAt.toISOString())}</span>
                </div>
            ),
        },
        {
            field: "audioData.size",
            headerName: "FILE SIZE",
            flex: 1.5,
            editable: false,
            sortable: false,
            disableColumnMenu: true,
            headerAlign: "left",
            align: "left",
            headerClassName: "datagrid-table--header",
            cellClassName: "datagrid-table--column-cell",
            renderCell: ({ row }: { row: TranscriptSectionIndexDB }) => (
                <div className="flex flex-col">
                    <span className="text-sm text-gray-900 font-medium">
                        {formatBytes(row.audioData?.size || 0, 1)}
                    </span>
                    {row.bytesUploaded < row.audioData.size && (
                        <span className="text-xs text-red-500 font-semibold">
                            {Math.round((row.bytesUploaded / row.audioData.size) * 100)}% uploaded
                        </span>
                    )}
                </div>
            ),
        },
        {
            field: "actions",
            type: "actions",
            headerName: "ACTIONS",
            flex: 1,
            headerAlign: "right",
            align: "right",
            sortable: false,
            disableColumnMenu: true,
            headerClassName: "datagrid-table--header",
            renderCell: (params) => {
                return (
                    <LocalRecordingActions
                        recording={params.row}
                        separateRetryButton={false}
                        showDeleteNow={true}
                        handleUpload={handleUpload}
                        handleDownload={handleDownload}
                        handleDelete={handleDelete}
                    />
                );
            },
        },
    ];

    return (
        <>
            <div>
                <div className="w-full flex items-center justify-between">
                    <div className="flex gap-2 items-baseline">
                        <h3 className="text-md font-semibold text-gray-900">Local Recordings</h3>
                        <Tooltip
                            content="This table shows all audio clips stored on your device. Audio clips are automatically deleted after one week (earlier if space is needed)."
                            anchorSelect="#local-recordings-tooltip"
                            style={{ maxWidth: "300px", zIndex: 9999 }}
                        />
                        <InfoCircleIcon
                            id="local-recordings-tooltip"
                            className={`h-[13px] w-[13px] mr-1 mt-1.5 focused `}
                        />
                    </div>

                    <div className="flex gap-2">
                        {VITE_PUBLIC_ENV !== "production" && (
                            <button
                                onClick={handleAddTestUpload}
                                className="flex items-center py-1 px-2 border rounded-md bg-blue-500 text-white shadow-sm hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
                            >
                                <span className="text-xs">Add Test</span>
                            </button>
                        )}
                        <button
                            onClick={() => {
                                window.open(
                                    "https://help.gohappydoc.com/en/articles/8563272-trouble-shooting-common-issues",
                                    "_blank",
                                    "noopener,noreferrer",
                                );
                            }}
                            className="flex items-center py-1 px-2 border rounded-md bg-white shadow-sm text-gray-900 focus:outline-none focused"
                        >
                            <HelpCircleIcon className={`h-[13px] w-[13px] mr-1`} />
                            <span className="text-xs">Help</span>
                        </button>
                    </div>
                </div>
            </div>
            <div className="flex flex-col w-full h-full max-h-[400px]">
                <DataGrid
                    getRowId={(row: TranscriptSectionIndexDB) => row.transcriptSectionId}
                    rows={recordings}
                    columns={columns}
                    editMode="row"
                    checkboxSelection={false}
                    hideFooter={true}
                    getRowClassName={() => "datagrid-table--row"}
                    getCellClassName={() => "datagrid-table--cell"}
                    sx={getDataGridStyles(false)}
                />
            </div>
        </>
    );
};

export default LocalRecordings;
